import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
    ArrowLeft, Clock, Calendar, Eye, Share2, Twitter, Linkedin,
    Link2, Check, ChevronRight, BookOpen, Tag, User
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { blogAPI } from '../utils/api';

const BlogDetailPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [readingProgress, setReadingProgress] = useState(0);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        fetchPost();
    }, [slug]);

    useEffect(() => {
        const handleScroll = () => {
            const article = document.getElementById('article-content');
            if (!article) return;

            const rect = article.getBoundingClientRect();
            const scrolled = window.scrollY;
            const articleTop = rect.top + scrolled - 200;
            const articleHeight = article.offsetHeight;
            const progress = Math.min(100, Math.max(0, ((scrolled - articleTop) / articleHeight) * 100));
            setReadingProgress(progress);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [post]);

    const fetchPost = async () => {
        try {
            setLoading(true);
            const data = await blogAPI.getPostBySlug(slug);
            setPost(data);
        } catch (error) {
            console.error('Error fetching post:', error);
        } finally {
            setLoading(false);
        }
    };

    const shareOnTwitter = () => {
        const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`;
        window.open(url, '_blank');
    };

    const shareOnLinkedIn = () => {
        const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`;
        window.open(url, '_blank');
    };

    const copyLink = async () => {
        await navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#030712] pt-24 flex items-center justify-center">
                <div className="w-10 h-10 border-2 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin" />
            </div>
        );
    }

    if (!post) {
        return (
            <div className="min-h-screen bg-[#030712] pt-24 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-white mb-4">Article not found</h2>
                    <Link to="/blog" className="text-emerald-400 hover:underline">
                        ← Back to Blog
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>{post.title} | Rastor AI Blog</title>
                <meta name="description" content={post.meta_description || post.excerpt} />
                <meta name="keywords" content={post.keywords?.join(', ')} />
                <meta property="og:title" content={post.title} />
                <meta property="og:description" content={post.meta_description || post.excerpt} />
                <meta property="og:type" content="article" />
                <link rel="canonical" href={`https://rastorai.com/blog/${post.slug}`} />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Article",
                        "headline": post.title,
                        "description": post.meta_description || post.excerpt,
                        "author": { "@type": "Organization", "name": "Rastor AI" },
                        "publisher": { "@type": "Organization", "name": "Rastor AI" },
                        "datePublished": post.created_at,
                        "mainEntityOfPage": `https://rastorai.com/blog/${post.slug}`
                    })}
                </script>
            </Helmet>

            {/* Reading Progress Bar */}
            <div className="fixed top-0 left-0 right-0 h-1 bg-white/5 z-50">
                <motion.div
                    className="h-full bg-gradient-to-r from-emerald-500 to-teal-400"
                    style={{ width: `${readingProgress}%` }}
                />
            </div>

            <div className="min-h-screen bg-[#030712] pt-24 pb-20">
                {/* Hero Header */}
                <section className="relative px-4 py-16 overflow-hidden">
                    {/* Background */}
                    <div className="absolute inset-0">
                        <div className="absolute top-10 left-1/3 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px]" />
                        <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-teal-500/10 rounded-full blur-[100px]" />
                    </div>

                    <div className="max-w-4xl mx-auto relative z-10">
                        {/* Breadcrumb */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mb-8"
                        >
                            <Link
                                to="/blog"
                                className="inline-flex items-center space-x-2 text-slate-400 hover:text-white transition-colors group"
                            >
                                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                                <span>Back to Blog</span>
                            </Link>
                        </motion.div>

                        {/* Category & Meta */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="flex flex-wrap items-center gap-3 mb-6"
                        >
                            <span className="px-4 py-1.5 bg-emerald-500/10 border border-emerald-500/40 rounded-full text-sm font-semibold text-emerald-400 capitalize">
                                {post.category?.replace('-', ' ')}
                            </span>
                            <span className="flex items-center space-x-2 text-sm text-slate-500">
                                <Clock className="w-4 h-4" />
                                <span>{post.read_time_minutes} min read</span>
                            </span>
                            <span className="flex items-center space-x-2 text-sm text-slate-500">
                                <Eye className="w-4 h-4" />
                                <span>{post.views || 0} views</span>
                            </span>
                        </motion.div>

                        {/* Title */}
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.15 }}
                            className="text-3xl md:text-5xl font-bold text-white leading-tight mb-6"
                        >
                            {post.title}
                        </motion.h1>

                        {/* Author & Date */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="flex items-center space-x-4"
                        >
                            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center">
                                <User className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <p className="font-semibold text-white">{post.author || 'Rastor AI AI'}</p>
                                <p className="text-sm text-slate-500">
                                    {new Date(post.created_at).toLocaleDateString('en-IN', {
                                        day: 'numeric', month: 'long', year: 'numeric'
                                    })}
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* Content & Sidebar */}
                <section className="px-4">
                    <div className="max-w-6xl mx-auto">
                        <div className="flex flex-col lg:flex-row gap-12">
                            {/* Main Content */}
                            <motion.article
                                id="article-content"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.25 }}
                                className="flex-1 max-w-3xl"
                            >
                                <div className="prose prose-invert prose-lg max-w-none
                  prose-headings:text-white prose-headings:font-bold
                  prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h2:border-l-4 prose-h2:border-emerald-500 prose-h2:pl-4
                  prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                  prose-p:text-slate-300 prose-p:leading-relaxed
                  prose-a:text-emerald-400 prose-a:no-underline hover:prose-a:underline
                  prose-strong:text-white
                  prose-ul:text-slate-300 prose-ol:text-slate-300
                  prose-li:marker:text-emerald-500
                  prose-blockquote:border-emerald-500 prose-blockquote:bg-emerald-500/5 prose-blockquote:rounded-r-lg prose-blockquote:py-1
                  prose-code:text-emerald-400 prose-code:bg-emerald-500/10 prose-code:px-2 prose-code:py-0.5 prose-code:rounded
                  prose-pre:bg-[#0A0F1A] prose-pre:border prose-pre:border-white/10"
                                >
                                    <ReactMarkdown>{post.content}</ReactMarkdown>
                                </div>

                                {/* Blog Post CTA Injection */}
                                <div className="mt-10 bg-gradient-to-r from-emerald-500/10 via-teal-500/5 to-transparent border border-emerald-500/20 rounded-2xl p-8">
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center shrink-0">
                                            <ChevronRight className="w-6 h-6 text-emerald-400" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="text-xl font-bold text-white mb-2">
                                                Ready to start your {
                                                    post.title?.toLowerCase().includes('data') ? 'data science' :
                                                        post.title?.toLowerCase().includes('ai') || post.title?.toLowerCase().includes('machine') ? 'AI/ML' :
                                                            post.title?.toLowerCase().includes('web') || post.title?.toLowerCase().includes('frontend') ? 'web development' :
                                                                post.title?.toLowerCase().includes('design') ? 'design' :
                                                                    'career'
                                                } journey?
                                            </h3>
                                            <p className="text-slate-400 text-sm mb-4">
                                                Get a free, AI-powered career analysis and personalized roadmap in under 2 minutes. No credit card required.
                                            </p>
                                            <Link
                                                to="/build-roadmap"
                                                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-emerald-500/30 transition-all"
                                            >
                                                <span>Get Your Free Career Analysis →</span>
                                            </Link>
                                        </div>
                                    </div>
                                </div>

                                {/* Tags */}
                                {post.keywords?.length > 0 && (
                                    <div className="mt-10 pt-8 border-t border-white/10">
                                        <div className="flex items-center space-x-2 mb-4">
                                            <Tag className="w-4 h-4 text-slate-500" />
                                            <span className="text-sm text-slate-500">Tags</span>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {post.keywords.map((keyword, i) => (
                                                <span
                                                    key={i}
                                                    className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-sm text-slate-400"
                                                >
                                                    {keyword}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </motion.article>

                            {/* Sidebar */}
                            <motion.aside
                                initial={{ opacity: 0, x: 30 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.3 }}
                                className="lg:w-80"
                            >
                                <div className="lg:sticky lg:top-28 space-y-6">
                                    {/* Share Card */}
                                    <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6">
                                        <h4 className="font-semibold text-white mb-4 flex items-center space-x-2">
                                            <Share2 className="w-4 h-4 text-emerald-400" />
                                            <span>Share Article</span>
                                        </h4>
                                        <div className="flex gap-3">
                                            <button
                                                onClick={shareOnTwitter}
                                                className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-[#1DA1F2]/10 hover:bg-[#1DA1F2]/20 border border-[#1DA1F2]/30 rounded-xl text-[#1DA1F2] transition-colors"
                                            >
                                                <Twitter className="w-4 h-4" />
                                                <span className="text-sm">Twitter</span>
                                            </button>
                                            <button
                                                onClick={shareOnLinkedIn}
                                                className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-[#0A66C2]/10 hover:bg-[#0A66C2]/20 border border-[#0A66C2]/30 rounded-xl text-[#0A66C2] transition-colors"
                                            >
                                                <Linkedin className="w-4 h-4" />
                                                <span className="text-sm">LinkedIn</span>
                                            </button>
                                        </div>
                                        <button
                                            onClick={copyLink}
                                            className="w-full mt-3 flex items-center justify-center space-x-2 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-slate-400 transition-colors"
                                        >
                                            {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Link2 className="w-4 h-4" />}
                                            <span className="text-sm">{copied ? 'Copied!' : 'Copy Link'}</span>
                                        </button>
                                    </div>

                                    {/* CTA Card */}
                                    <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/5 border border-emerald-500/30 rounded-2xl p-6">
                                        <h4 className="font-bold text-white text-lg mb-2">Get Your Career Roadmap</h4>
                                        <p className="text-sm text-slate-400 mb-4">
                                            Personalized AI guidance for your dream career path.
                                        </p>
                                        <Link
                                            to="/profile"
                                            className="flex items-center justify-center space-x-2 w-full py-3 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-xl transition-colors"
                                        >
                                            <span>Start Free</span>
                                            <ChevronRight className="w-4 h-4" />
                                        </Link>
                                    </div>

                                    {/* More Articles */}
                                    <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6">
                                        <h4 className="font-semibold text-white mb-4 flex items-center space-x-2">
                                            <BookOpen className="w-4 h-4 text-emerald-400" />
                                            <span>More Articles</span>
                                        </h4>
                                        <Link
                                            to="/blog"
                                            className="flex items-center justify-between text-slate-400 hover:text-white transition-colors py-2"
                                        >
                                            <span className="text-sm">Browse all articles</span>
                                            <ChevronRight className="w-4 h-4" />
                                        </Link>
                                    </div>
                                </div>
                            </motion.aside>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default BlogDetailPage;
